import { CODIGOS_ERROR, type RespuestaError } from '@cmms/shared';
import { notificarExitoGlobal } from '../componentes/notificaciones-globales.js';

/**
 * Cliente de la API.
 *
 * Las cookies httpOnly llevan la sesión, así que aquí no se guarda ningún token:
 * no hay nada en `localStorage` que un XSS pueda leer. Por eso toda petición va
 * con `credentials: 'include'`.
 */
export class ErrorApi extends Error {
  constructor(
    readonly codigo: string,
    mensaje: string,
    readonly estado: number,
    readonly detalles?: unknown,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }
}

const BASE = '/api';

/**
 * Token de la tablet emparejada (kiosko). Solo viaja en las rutas `/movil`; la tablet no tiene
 * sesión de usuario, así que esas rutas no renuevan ni cierran sesiones.
 */
let tokenMovil: string | null = null;

export function fijarTokenMovil(token: string | null): void {
  tokenMovil = token;
}

/**
 * Renovación del token de acceso.
 *
 * Se guarda la promesa en curso para que varias peticiones que caduquen a la vez
 * no disparen varias rotaciones: la segunda invalidaría a la primera y el
 * servidor lo leería como reuso de token, cerrando la sesión del usuario.
 */
const renovacionEnCurso: Record<string, Promise<boolean> | null> = {};

/**
 * Tenant y plataforma son dos sesiones distintas, con cookies y rutas de
 * renovación propias. Se elige según el prefijo de la ruta que caducó: renovar
 * la equivocada devolvería 401 y cerraría una sesión que estaba sana.
 */
function rutaDeRenovacion(ruta: string): string {
  return ruta.startsWith('/plataforma/') ? '/plataforma/auth/renovar' : '/auth/renovar';
}

async function renovar(rutaRenovacion: string): Promise<boolean> {
  renovacionEnCurso[rutaRenovacion] ??= fetch(`${BASE}${rutaRenovacion}`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      renovacionEnCurso[rutaRenovacion] = null;
    });
  return renovacionEnCurso[rutaRenovacion]!;
}

/**
 * Aviso de sesión vencida — §"401 que deja la pantalla colgada sin mensaje".
 *
 * `api.ts` no tiene acceso al contexto de React (se crea antes que cualquier
 * componente), así que cada proveedor de sesión (`sesion.tsx`,
 * `sesion-operador.tsx`) registra aquí cómo limpiar su propio estado. Tenant y
 * plataforma son sesiones independientes: un 401 de una no debe tocar la otra.
 */
type OyenteSesionExpirada = () => void;
const oyentesSesionExpirada: Record<'tenant' | 'plataforma', OyenteSesionExpirada | null> = {
  tenant: null,
  plataforma: null,
};

export function registrarSesionExpirada(ambito: 'tenant' | 'plataforma', oyente: OyenteSesionExpirada | null): void {
  oyentesSesionExpirada[ambito] = oyente;
}

let redirigiendoAlNucleo = false;

/**
 * La sesión legada caducó por el corte de la doble aceptación
 * (`LEGACY_TOKENS_ACCEPTED_UNTIL`): renovarla no sirve —el refresco reemite otra
 * legada— y el mensaje del servidor pide entrar «desde el acceso vigente». Se
 * cumple aquí, con la misma navegación de página completa que
 * `iniciarConNucleo` (`sesion.tsx`), y una sola vez aunque fallen varias
 * peticiones a la vez.
 */
function irAlAccesoPorContinnua(): void {
  if (redirigiendoAlNucleo) return;
  redirigiendoAlNucleo = true;
  window.location.assign('/api/auth/nucleo/inicio');
}

function esRutaMovil(ruta: string): boolean {
  return ruta.startsWith('/movil/') || ruta === '/movil';
}

async function ejecutar<T>(ruta: string, opciones: RequestInit, reintentar = true): Promise<T> {
  const respuesta = await fetch(`${BASE}${ruta}`, {
    ...opciones,
    credentials: 'include',
    headers: {
      // FormData fija su propio Content-Type con el boundary del multipart:
      // forzar aquí application/json rompería la subida.
      ...(typeof opciones.body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...(esRutaMovil(ruta) && tokenMovil ? { Authorization: `Bearer ${tokenMovil}` } : {}),
      ...opciones.headers,
    },
  });

  if (respuesta.status === 204) return undefined as T;

  const cuerpo: unknown = await respuesta.json().catch(() => null);

  // Un 401 en el propio inicio de sesión son credenciales malas, no un token
  // vencido: renovar allí solo añadiría una petición inútil. Lo mismo si el
  // cuerpo ya dice que las credenciales (o el refresh) no son válidas: renovar
  // no lo va a arreglar y solo gasta una petición más.
  const esRutaDeSesion = ruta.startsWith('/auth/') || ruta.startsWith('/plataforma/auth/');
  const credencialesInvalidas = (cuerpo as Partial<RespuestaError> | null)?.codigo === 'CREDENCIALES_INVALIDAS';
  if (respuesta.status === 401 && reintentar && !esRutaDeSesion && !credencialesInvalidas && !esRutaMovil(ruta)) {
    if (await renovar(rutaDeRenovacion(ruta))) return ejecutar<T>(ruta, opciones, false);
  }

  if (!respuesta.ok) {
    const error = (cuerpo ?? {}) as Partial<RespuestaError>;
    if (respuesta.status === 401 && error.codigo === CODIGOS_ERROR.SESION_LEGADA_CADUCADA) {
      irAlAccesoPorContinnua();
    } else if (respuesta.status === 401 && !esRutaDeSesion && !esRutaMovil(ruta)) {
      // La renovación de arriba ya se intentó y falló: no hay forma de seguir
      // en esta sesión. Sin este aviso, el error queda en el `query`/`mutation`
      // que lo lanzó y la pantalla se queda como estaba, sin explicar por qué.
      oyentesSesionExpirada[ruta.startsWith('/plataforma/') ? 'plataforma' : 'tenant']?.();
    }
    throw new ErrorApi(
      error.codigo ?? 'ERROR',
      error.mensaje ?? 'No se pudo completar la operación.',
      respuesta.status,
      error.detalles,
    );
  }

  // El mensaje de confirmación lo redacta el backend con `@MensajeExito(...)`
  // (ver `interceptor-mensaje-exito.ts`) y viaja en un header, no en el
  // cuerpo: así ningún tipo de respuesta cambia y ninguna página tiene que
  // acordarse de mostrar el suyo — el mismo trato genérico que ya tiene el
  // error del lado contrario.
  const mensajeExito = respuesta.headers.get('X-Mensaje-Exito');
  if (mensajeExito) notificarExitoGlobal(decodeURIComponent(mensajeExito));

  return cuerpo as T;
}

export const api = {
  get: <T>(ruta: string): Promise<T> => ejecutar<T>(ruta, { method: 'GET' }),
  post: <T>(ruta: string, cuerpo?: unknown): Promise<T> =>
    ejecutar<T>(ruta, { method: 'POST', body: cuerpo === undefined ? undefined : JSON.stringify(cuerpo) }),
  put: <T>(ruta: string, cuerpo: unknown): Promise<T> =>
    ejecutar<T>(ruta, { method: 'PUT', body: JSON.stringify(cuerpo) }),
  patch: <T>(ruta: string, cuerpo: unknown): Promise<T> =>
    ejecutar<T>(ruta, { method: 'PATCH', body: JSON.stringify(cuerpo) }),
  delete: <T>(ruta: string): Promise<T> => ejecutar<T>(ruta, { method: 'DELETE' }),
  subirArchivo: <T>(ruta: string, formData: FormData): Promise<T> =>
    ejecutar<T>(ruta, { method: 'POST', body: formData }),
};

/**
 * Descarga un archivo generado por la API (Excel, CSV, PDF) y lo entrega al
 * navegador con el nombre que indica `Content-Disposition`. No pasa por
 * `ejecutar` porque la respuesta no es JSON.
 */
export async function descargarArchivo(ruta: string): Promise<void> {
  const respuesta = await fetch(`${BASE}${ruta}`, { credentials: 'include' });
  if (!respuesta.ok) {
    const error = (await respuesta.json().catch(() => null)) as Partial<RespuestaError> | null;
    throw new ErrorApi(
      error?.codigo ?? 'ERROR',
      error?.mensaje ?? 'No se pudo exportar.',
      respuesta.status,
      error?.detalles,
    );
  }
  const nombre = /filename="([^"]+)"/.exec(respuesta.headers.get('Content-Disposition') ?? '')?.[1] ?? 'exportacion';
  const url = URL.createObjectURL(await respuesta.blob());
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombre;
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  URL.revokeObjectURL(url);
}

/** Construye una query descartando lo vacío, para no ensuciar la URL. */
export function consulta(params: Record<string, unknown>): string {
  const busqueda = new URLSearchParams();
  for (const [clave, valor] of Object.entries(params)) {
    if (valor === undefined || valor === null || valor === '') continue;
    busqueda.set(clave, Array.isArray(valor) ? valor.join(',') : String(valor));
  }
  const texto = busqueda.toString();
  return texto ? `?${texto}` : '';
}
